/**
 * 迁移配置
 * 控制哪些事件/页面/场景使用新架构
 */

export interface MigrationConfig {
  // 全局配置
  global: {
    enabled: boolean; // 全局开关
    dualWrite: boolean; // 全局双写开关
    sampleRate: number; // 全局采样率（控制新架构的流量）
  };

  // 事件迁移配置
  events: {
    [eventName: string]: {
      enabled: boolean; // 是否启用新架构（上报到新表）
      dualWrite: boolean; // 是否双写（同时上报新旧两份数据）
      sampleRate?: number; // 采样率 [0-1]，用于灰度（控制新架构的流量）
    };
  };

  // 页面迁移配置
  pages: {
    [pageName: string]: {
      enabled: boolean;
      dualWrite: boolean;
      sampleRate?: number;
    };
  };
}

// 默认配置：所有事件都使用旧架构
export const defaultMigrationConfig: MigrationConfig = {
  global: {
    enabled: false, // 默认关闭
    dualWrite: false,
    sampleRate: 1,
  },
  events: {},
  pages: {},
};

// 当前配置（可通过 localStorage 或服务端获取）
let currentConfig: MigrationConfig = defaultMigrationConfig;

/**
 * 从 localStorage 或服务端获取配置
 */
export function getMigrationConfig(): MigrationConfig {
  try {
    if (typeof localStorage !== "undefined") {
      const config = localStorage.getItem("tracker_v2_migration_config");
      if (config) {
        const parsed = JSON.parse(config);
        currentConfig = { ...defaultMigrationConfig, ...parsed };

        if (process.env.NODE_ENV === "development") {
          console.log(
            "[Migration] Loaded config from localStorage:",
            currentConfig
          );
        }

        return currentConfig;
      }
    }
  } catch (error) {
    console.error("[Migration] Load config error:", error);
  }

  return currentConfig;
}

/**
 * 保存配置
 */
export function setMigrationConfig(config: MigrationConfig): void {
  currentConfig = config;

  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(
        "tracker_v2_migration_config",
        JSON.stringify(config)
      );

      if (process.env.NODE_ENV === "development") {
        console.log("[Migration] Saved config to localStorage:", config);
      }
    }
  } catch (error) {
    console.error("[Migration] Save config error:", error);
  }
}

/**
 * 检查事件是否应该使用新架构
 *
 * ⚠️ 重要说明：
 * - useV2: 是否使用新架构上报到新表
 * - dualWrite: 是否双写（新架构和旧架构同时上报）
 * - 旧埋点代码完全不动，继续走原有流程上报到旧表
 * - 双写不是分流，而是同时上报两份数据到两个表
 *
 * 优先级：事件配置 > 页面配置 > 全局配置
 */
export function shouldUseV2(
  eventName: string,
  pageName?: string
): {
  useV2: boolean; // 是否使用新架构
  dualWrite: boolean; // 是否双写（同时上报新旧两份数据）
} {
  const config = getMigrationConfig();

  // 1. 优先检查事件配置（不受全局开关影响）
  const eventConfig = config.events[eventName];
  if (eventConfig && eventConfig.enabled) {
    const useV2 = (eventConfig.sampleRate ?? 1) >= Math.random();
    return {
      useV2,
      dualWrite: useV2 && eventConfig.dualWrite,
    };
  }

  // 2. 检查页面配置（不受全局开关影响）
  if (pageName) {
    const pageConfig = config.pages[pageName];
    if (pageConfig && pageConfig.enabled) {
      const useV2 = (pageConfig.sampleRate ?? 1) >= Math.random();
      return {
        useV2,
        dualWrite: useV2 && pageConfig.dualWrite,
      };
    }
  }

  // 3. 检查全局配置
  if (!config.global.enabled) {
    return { useV2: false, dualWrite: false };
  }

  const useV2 = config.global.sampleRate >= Math.random();
  return {
    useV2,
    dualWrite: useV2 && config.global.dualWrite,
  };
}

/**
 * 启用特定事件的新架构
 */
export function enableEventV2(
  eventName: string,
  options: {
    dualWrite?: boolean;
    sampleRate?: number;
  } = {}
): void {
  const config = getMigrationConfig();

  config.events[eventName] = {
    enabled: true,
    dualWrite: options.dualWrite ?? false,
    sampleRate: options.sampleRate ?? 1,
  };

  setMigrationConfig(config);

  if (process.env.NODE_ENV === "development") {
    console.log(`[Migration] Enabled V2 for event: ${eventName}`, options);
  }
}

/**
 * 启用特定页面的新架构
 */
export function enablePageV2(
  pageName: string,
  options: {
    dualWrite?: boolean;
    sampleRate?: number;
  } = {}
): void {
  const config = getMigrationConfig();

  config.pages[pageName] = {
    enabled: true,
    dualWrite: options.dualWrite ?? false,
    sampleRate: options.sampleRate ?? 1,
  };

  setMigrationConfig(config);

  if (process.env.NODE_ENV === "development") {
    console.log(`[Migration] Enabled V2 for page: ${pageName}`, options);
  }
}

/**
 * 启用全局新架构
 */
export function enableGlobalV2(
  options: {
    dualWrite?: boolean;
    sampleRate?: number;
  } = {}
): void {
  const config = getMigrationConfig();

  config.global = {
    enabled: true,
    dualWrite: options.dualWrite ?? false,
    sampleRate: options.sampleRate ?? 1,
  };

  setMigrationConfig(config);

  if (process.env.NODE_ENV === "development") {
    console.log("[Migration] Enabled global V2:", options);
  }
}

/**
 * 关闭特定事件的新架构
 */
export function disableEventV2(eventName: string): void {
  const config = getMigrationConfig();

  if (config.events[eventName]) {
    config.events[eventName].enabled = false;
    setMigrationConfig(config);

    if (process.env.NODE_ENV === "development") {
      console.log(`[Migration] Disabled V2 for event: ${eventName}`);
    }
  }
}

/**
 * 关闭全局新架构
 */
export function disableGlobalV2(): void {
  const config = getMigrationConfig();
  config.global.enabled = false;
  setMigrationConfig(config);

  if (process.env.NODE_ENV === "development") {
    console.log("[Migration] Disabled global V2");
  }
}

/**
 * 清除迁移配置（用于测试）
 */
export function clearMigrationConfig(): void {
  currentConfig = { ...defaultMigrationConfig };

  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("tracker_v2_migration_config");
    }
  } catch (error) {
    console.error("[Migration] Clear config error:", error);
  }
}

// 初始化时加载配置
getMigrationConfig();
